<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerText"
    :disabled="status.isLoading"
    :hide-reset="!!rideInfo"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Update Uber & Lyft Ride Destination
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Request ID*</label>
        <div class="col-sm-8">
          <sanitized-input
            :id="`requestId-${pageId}`"
            v-model="requestId"
            placeholder="Please input request id"
            required
            :disabled="!!rideInfo"
          />
        </div>
      </div>

      <go-go-google-map
        map-type-id="satellite"
        class="mb-10 mt-10 mobile-mt-10"
        :markers="[...markers, carMapPin]"
        :map-center="mapCenter"
        :is-draggable="true"
        :zoom="18"
        :radius="500"
        :is-side-by-side="true"
        @change="setMarkerPosition"
      />

      <ordering-address
        v-if="status.isFetchedRideInfo"
        v-model="endAddress"
        :index="1"
        :is-venues-search="false"
        :is-swap-location="false"
        is-geocode-show
        :page-id="pageId"
        :anchor-bound="endAddressAnchorBound"
        class="mb-10"
      >
        <template #title>
          <div class="mb-2">
            End Address*
          </div>
        </template>
      </ordering-address>
    </template>

    <message-html :result-html="status.resultHtml" />
    <template #submit-button>
      {{ status.isFetchedRideInfo ? 'Update Destination' : 'Get Ride Info' }}
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, watch, computed, onMounted } from 'vue';
import events from '@/events';

import util from '@/Utils/util';
import { sendPost } from '@/Utils/api';
import { useCustomer } from '@/use/customer';
import { useAddress } from '@/use/address';
import { emptyAddress } from '@/constants/ride';
import { GeoLocationType, LocationDataType, CustomLocationType } from '@/models/address';
import { rideService } from '@/services/ride';

const pageId = 'uber-ride-destination';

const props = defineProps({
  rideInfo: {
    type: Object,
    default: () => null,
  },
});

const { lastRideInfo } = useCustomer();
const { getAddressObjectFromString, getAnchorBound, updateAddressWithGeocode } = useAddress();

const requestId = ref('');
const endAddress: Ref<CustomLocationType> = ref({ ...emptyAddress });
const originalEndAddress: Ref<CustomLocationType> = ref({ ...emptyAddress });

const mapCenter: Ref<GeoLocationType> = ref({
  lat: 37.42248,
  lng: -122.08547,
});
const markers: Ref<any> = ref([]);
const carMapPin = ref({
  position: {
    lat: 37.42248,
    lng: -122.08547,
  },
  index: 100,
  isShow: false,
  isDraggable: false,
  icon: '/static/img/car_marker.png',
});
const status = reactive({
  isLoading: false,
  isFetchedRideInfo: false,
  spinnerText: '',
  resultHtml: '',
});

const endAddressAnchorBound = computed(() => getAnchorBound(originalEndAddress.value));

const submitForm = () => {
  status.resultHtml = '';

  if (status.isFetchedRideInfo) {
    updateDestination();
  } else {
    fetchRideInfo();
  }
};

const setMarkerPosition = (
  lat: number | string | null,
  lng: number | string | null,
  index: number,
  isMarkerChanged: boolean = true,
  isAddressChanged: boolean = false,
) => {
  util.updateMarkerPosition(markers.value[index], lat, lng, isAddressChanged);

  mapCenter.value = {
    lat: Number(lat) || mapCenter.value.lat,
    lng: Number(lng) || mapCenter.value.lng,
  };

  if (isMarkerChanged) {
    endAddress.value = updateAddressWithGeocode(endAddress.value, lat, lng);
  }
};

const fetchRideInfo = async () => {
  status.spinnerText = 'Loading Ride Info...';
  markers.value = [];
  status.isFetchedRideInfo = false;
  endAddress.value = { ...emptyAddress };
  status.isLoading = true;

  try {
    const rideInfo = await rideService.getActiveRideWithIsVendorRide(requestId.value, true);

    if (!rideInfo) {
      status.resultHtml = util.getErrorResponse({ message: `There is no ride with ${requestId.value}` });
      status.isLoading = false;
      return;
    }

    if (rideInfo.isVendorRide === true && rideInfo.orderedBy === 'scheduler') {
      // need check "rideInfo.orderedBy === 'scheduler'" because auto re-ordered rides are not vendor scheduled rides!
      status.resultHtml = util.getErrorResponse({ message: 'Vendor Scheduled Rides destinations cannot be updated because these rides are scheduled with an upfront price estimate.' });
      status.isLoading = false;
      return;
    }

    setEndAddressAndMarkers(rideInfo);
    status.isFetchedRideInfo = true;
  } catch (error) {
    status.resultHtml = util.getErrorResponse(error);
  }
  status.isLoading = false;
};

const setEndAddressAndMarkers = (rideInfo: any) => {
  const { curLat, curLng, startLat, startLong, destination, destinationLat, destinationLng, endLocation, endLat, endLong } = rideInfo;

  if (curLat && curLng) {
    carMapPin.value = {
      ...carMapPin.value,
      position: {
        lat: Number(curLat),
        lng: Number(curLng),
      },
      isShow: true,
    };
  }

  markers.value.push({
    position: { lat: Number(startLat), lng: Number(startLong) },
    initialPosition: { lat: Number(startLat), lng: Number(startLong) },
    index: 0,
    isShow: true,
    isDraggable: false,
    icon: '/static/img/pickup_marker.png',
  });

  mapCenter.value = markers.value[0].position;

  const address = getAddressObjectFromString(destination || endLocation || '') as LocationDataType;

  const latitude = destinationLat || endLat ? Number(destinationLat || endLat) : null;
  const longitude = destinationLng || endLong ? Number(destinationLng || endLong) : null;

  markers.value.push({
    position: {
      lat: latitude,
      lng: longitude,
    },
    initialPosition: {
      lat: latitude,
      lng: longitude,
    },
    index: 1,
    isDraggable: true,
    isShow: true,
    icon: '/static/img/dropoff_marker.png',
  });

  endAddress.value = {
    streetAddress: address.streetAddress,
    streetAddress1: '',
    city: address.city,
    state: address.state,
    zip: address.zip,
    country: address.country,
    name: '',
    latitude,
    longitude,
    type: false,
    index: 1,
  };

  originalEndAddress.value = { ...endAddress.value };
};

const updateDestination = async () => {
  status.spinnerText = 'Updating Destinations...';
  status.isLoading = true;

  try {
    await sendPost('updateRideDestination', {
      requestId: requestId.value,
      streetAddress: endAddress.value.streetAddress,
      city: endAddress.value.city,
      state: endAddress.value.state,
      zip: endAddress.value.zip,
      latitude: endAddress.value.latitude,
      longitude: endAddress.value.longitude,
    });

    status.resultHtml = util.setSuccessMessage('Ride destination is updated successfully.');
    events.emit('refresh-right-panel');
  } catch (error) {
    status.resultHtml = util.getErrorResponse(error);
  }
  status.isLoading = false;
};

const resetForm = () => {
  status.resultHtml = '';
  requestId.value = '';
  status.isFetchedRideInfo = false;
  markers.value = [];
  carMapPin.value.isShow = false;
};

watch(lastRideInfo, (value: any) => {
  if (props.rideInfo) {
    return;
  }

  resetForm();
  requestId.value = value?.requestId;
});

watch(endAddress, (value: CustomLocationType, oldValue: CustomLocationType) => {
  if (!value.streetAddress) {
    return;
  }

  const isAddressChanged = util.isAddressChanged(value, oldValue);
  const { latitude, longitude } = value;

  setMarkerPosition(Number(latitude), Number(longitude), 1, false, isAddressChanged);
});

onMounted(() => {
  if (props.rideInfo) {
    requestId.value = props.rideInfo.requestId;
    setEndAddressAndMarkers(props.rideInfo);
  } else {
    requestId.value = lastRideInfo.value?.requestId;
  }
});
</script>
